import { EditorState, RichUtils, Modifier, SelectionState } from 'draft-js';
import { EditorPlugin } from '@draft-js-plugins/editor';

export enum TextStyles {
  BOLD = 'BOLD',
  ITALIC = 'ITALIC',
}

export enum ShortcutCommands {
  BOLD = 'SHORTCUT_BOLD',
  ITALIC = 'SHORTCUT_ITALIC',
  DELETE_MENTION = 'DELETE_MENTION',
}

export enum ShortcutCommandResults {
  HANDLED = 'handled',
  NOT_HANDLED = 'not-handled',
}

export enum eventKeys {
  BACKSPACE = 'Backspace',
  CTRL = 'Ctrl',
  META = 'Meta',
  B = 'b',
  I = 'i',
}

export const createToggleStyle = (
  getEditorState: (() => EditorState),
  setEditorState: ((state: EditorState) => void),
) => (style: TextStyles) => {
  const stateWithModifiedStyle = RichUtils.toggleInlineStyle(getEditorState(), style);
  setEditorState(stateWithModifiedStyle);
};

const handleBackspaceOnMention = (
  editorState: EditorState,
  setEditorState: (state: EditorState) => void,
): ShortcutCommandResults => {
  const selection = editorState.getSelection();
  const content = editorState.getCurrentContent();

  if (!selection.isCollapsed()) {
    return ShortcutCommandResults.NOT_HANDLED;
  }

  const anchorKey = selection.getAnchorKey();
  const anchorOffset = selection.getAnchorOffset();

  if (anchorOffset === 0) {
    return ShortcutCommandResults.NOT_HANDLED;
  }

  const block = content.getBlockForKey(anchorKey);
  const blockText = block.getText();

  let entityKeyBefore = block.getEntityAt(anchorOffset - 1);
  let checkOffset = anchorOffset - 1;

  if (!entityKeyBefore && checkOffset > 0 && blockText[checkOffset] === ' ') {
    entityKeyBefore = block.getEntityAt(checkOffset - 1);
    checkOffset -= 1;
  }

  if (!entityKeyBefore) {
    return ShortcutCommandResults.NOT_HANDLED;
  }

  const entity = content.getEntity(entityKeyBefore);

  if (entity.getType() !== 'mention') {
      return ShortcutCommandResults.NOT_HANDLED;
  }

  let mentionStart = checkOffset;
  let mentionEnd = checkOffset + 1;

  while (mentionStart > 0 && block.getEntityAt(mentionStart - 1) === entityKeyBefore) {
    mentionStart--;
  }

  const blockLength = block.getLength();
  while (mentionEnd < blockLength && block.getEntityAt(mentionEnd) === entityKeyBefore) {
    mentionEnd++;
  }

  if (mentionEnd < blockLength && blockText[mentionEnd] === ' ') {
    mentionEnd++;
  }

  const mentionSelection = SelectionState.createEmpty(anchorKey).merge({
    anchorOffset: mentionStart,
    focusOffset: mentionEnd,
  });

  const newContent = Modifier.removeRange(content, mentionSelection, 'backward');
  const newEditorState = EditorState.push(editorState, newContent, 'remove-range');

  setEditorState(newEditorState);

  return ShortcutCommandResults.HANDLED;
};

export const createKeyboardShortcutsPlugin = (): EditorPlugin => ({
  keyBindingFn: (event, { getEditorState }) => {
    if (event.key === eventKeys.BACKSPACE) {
      const editorState = getEditorState();
      const selection = editorState.getSelection();

      if (!selection.isCollapsed()) {
        return undefined;
      }

      const anchorOffset = selection.getAnchorOffset();

      if (anchorOffset === 0) {
        return undefined;
      }

      const content = editorState.getCurrentContent();
      const anchorKey = selection.getAnchorKey();
      const block = content.getBlockForKey(anchorKey);
      const blockText = block.getText();

      let entityKeyBefore = block.getEntityAt(anchorOffset - 1);
      const checkOffset = anchorOffset - 1;

      if (!entityKeyBefore && checkOffset > 0 && blockText[checkOffset] === ' ') {
        entityKeyBefore = block.getEntityAt(checkOffset - 1);
      }

      if (entityKeyBefore) {
        const entity = content.getEntity(entityKeyBefore);
        if (entity.getType() === 'mention') {
          return ShortcutCommands.DELETE_MENTION;
        }
      }
    }

    if (event.ctrlKey || event.metaKey) {
      switch (event.key) {
        case 'b':
          return ShortcutCommands.BOLD;

        case 'i':
          return ShortcutCommands.ITALIC;

        default:
          break;
      }
    }

    return undefined;
  },
  handleKeyCommand: (
    command: ShortcutCommands | string,
    state,
    eventTimeStamp,
    { getEditorState, setEditorState },
  ) => {
    const toggleStyle = createToggleStyle(getEditorState, setEditorState);

    switch (command) {
      case ShortcutCommands.BOLD:
        toggleStyle(TextStyles.BOLD);
        return ShortcutCommandResults.HANDLED;

      case ShortcutCommands.ITALIC:
        toggleStyle(TextStyles.ITALIC);
        return ShortcutCommandResults.HANDLED;

      case ShortcutCommands.DELETE_MENTION:
        return handleBackspaceOnMention(getEditorState(), setEditorState);

      default:
        return ShortcutCommandResults.NOT_HANDLED;
    }
  },
});
